import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, forwardRef, Injector, Input, Output } from '@angular/core';
import { AbstractControl, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { isEqual } from 'lodash-es';
import { merge, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { IInputComponent } from '@nimbus-library/features/shared/components/material/i-input-component';
import { SecurityGroupInfoComponent } from '@nimbus-library/features/shared/components/material/input-administration-security-group/administration-group-info-dialog/security-group-info.component';
import { AdministrationGroupViewModel } from '@nimbus-library/features/shared/components/material/input-administration-security-group/models/administration-group.view-model';
import { SecurityGroupService } from '@nimbus-library/features/shared/components/material/input-administration-security-group/security-group.service';
import { IValidationMessagesDictionary } from '@nimbus-library/features/shared/directives/nimbus-input-validation.directive';
import { ValidGuidDirective } from '@nimbus-library/features/shared/directives/valid-guid.directive';
import { PermissionError } from '@nimbus-library/features/shared/models/api/graph-api-permission-exception.model';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-input-administration-security-group',
  templateUrl: './input-administration-security-group.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputAdministrationSecurityGroupComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => InputAdministrationSecurityGroupComponent),
    },
  ],
})
export class InputAdministrationSecurityGroupComponent extends IInputComponent implements AfterViewInit, Validator {
  @Input()
  public isOwnTenant: boolean = false;

  @Input()
  public isLoading: boolean = false;

  @Input()
  public showCopy: boolean = true;

  @Output()
  public isLoadingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  public tenantId: string;

  @Input()
  public parentSubject$: Subject<string> = new Subject<string>();

  @Output()
  public loadGroupData: EventEmitter<UntypedFormControl> = new EventEmitter<UntypedFormControl>();

  private readonly INPUT_DEBOUNCE: number = 550;

  public inputControl: UntypedFormControl = new UntypedFormControl();
  public groupViewModel: AdministrationGroupViewModel;
  public consentLink: string;

  public validationMessages: IValidationMessagesDictionary = {
    adminConsentRequired: () => '',
    administrationGroupNotFound: () => 'The group cannot be found',
  };

  public get isPermissionError(): boolean {
    return this.isErrorState() && Object.keys(this.ngControl.errors).includes('adminConsentRequired');
  }

  public constructor(
    protected changeDetectionRef: ChangeDetectorRef,
    protected injector: Injector,
    protected translateService: TranslateService,
    private readonly apiService: SecurityGroupService,
    private readonly matDialogService: MatDialog,
  ) {
    super(changeDetectionRef, injector, translateService);
  }

  public ngAfterViewInit(): void {
    if (!this.isDisabled || this.isOwnTenant) {
      if (this.inputControl.value) {
        this.getAdministrationGroupInformation(this.inputControl.value);
      }
      this.subscribeOnValueChanged();
    }
  }

  public validate(control: AbstractControl): { [key: string]: unknown } {
    return new ValidGuidDirective(null).validate(control);
  }

  public onDetailsClick(event: Event): void {
    event.stopPropagation();
    this.matDialogService.open(SecurityGroupInfoComponent, {
      data: this.groupViewModel,
      width: '600px',
    });
  }

  private getAdministrationGroupInformation(value: string): void {
    this.isLoadingChange.emit(true);
    if ((value && this.tenantId && this.inputControl.valid) || this.isOwnTenant) {
      this.apiService
        .getAdministrationGroupInformation(value, this.tenantId)
        .pipe(untilDestroyed(this))
        .subscribe(
          (result: AdministrationGroupViewModel) => {
            this.groupViewModel = result;
            this.isLoadingChange.emit(false);
            this.changeDetectionRef.detectChanges();
          },
          (error) => {
            if (error instanceof PermissionError) {
              this.ngControl.control.markAsDirty();
              this.ngControl.control.setErrors({ adminConsentRequired: error });
              this.consentLink = error.consentLink;
              // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            } else if (error.httpStatus === 403) {
              this.isLoadingChange.emit(false);
              return;
            } else {
              this.ngControl.control.markAsDirty();
              this.ngControl.control.setErrors({ administrationGroupNotFound: true });
            }

            this.groupViewModel = null;
            this.isLoadingChange.emit(false);
            this.changeDetectionRef.markForCheck();
          },
        );
    } else {
      this.groupViewModel = null;
      this.isLoadingChange.emit(false);
      this.changeDetectionRef.markForCheck();
    }
  }

  private subscribeOnValueChanged(): void {
    merge(...[this.inputControl.valueChanges.pipe(distinctUntilChanged(isEqual), debounceTime(this.INPUT_DEBOUNCE)), this.parentSubject$])
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        this.getAdministrationGroupInformation(value);
      });
  }
}
